import { useEffect, useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  listCompanyOptions,
  listDepartmentOptions,
  listRoleOptions,
  listUsers,
} from '../api/userApi'
import { getMasterApprovalSummary } from '../../../approval/masters/api/masterApprovalApi'

const DEFAULT_PAGE_SIZE = 10
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'

function buildSearchPayload(searchText, roleId, departmentId, companyId, roleOptions, departmentOptions, companyOptions) {
  const parts = [String(searchText || '').trim()].filter(Boolean)

  if (roleId) {
    const role = roleOptions.find((item) => Number(item.id) === Number(roleId))
    if (role?.name) parts.push(role.name)
  }

  if (departmentId) {
    const department = departmentOptions.find((item) => Number(item.id) === Number(departmentId))
    if (department?.name) parts.push(department.name)
  }

  if (companyId) {
    const company = companyOptions.find((item) => Number(item.id) === Number(companyId))
    if (company?.name) parts.push(company.name)
  }

  return parts.join(' ')
}

function toPhotoUrl(path) {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  if (path.startsWith('/')) return `${API_BASE_URL}${path}`
  return `${API_BASE_URL}/${path}`
}

function normalizeStatus(item) {
  if (Number(item?.is_archive) === 1) return { label: 'Inactive', tone: 'neutral' }
  if (Number(item?.is_approved) === 0 || item?.approval_status === 'PENDING') return { label: 'Pending Approval', tone: 'warning' }
  if (item?.approval_status === 'REJECTED') return { label: 'Rejected', tone: 'danger' }
  if (Number(item?.is_locked) === 1) return { label: 'Locked', tone: 'warning' }
  return { label: 'Active', tone: 'success' }
}

export function useUserListScreen() {
  const [searchText, setSearchText] = useState('')
  const [roleId, setRoleId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [companyId, setCompanyId] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(DEFAULT_PAGE_SIZE)

  const [roleOptions, setRoleOptions] = useState([])
  const [departmentOptions, setDepartmentOptions] = useState([])
  const [companyOptions, setCompanyOptions] = useState([])

  const [items, setItems] = useState([])
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0)

  useEffect(() => {
    let active = true

    async function loadOptions() {
      try {
        const [roles, departments, companies] = await Promise.all([
          listRoleOptions({ page: 1, page_size: 200 }),
          listDepartmentOptions({ page: 1, page_size: 200 }),
          listCompanyOptions({ page: 1, page_size: 200 }),
        ])
        if (!active) return
        setRoleOptions(roles)
        setDepartmentOptions(departments)
        setCompanyOptions(companies)
      } catch {
        if (!active) return
        setRoleOptions([])
        setDepartmentOptions([])
        setCompanyOptions([])
      }
    }

    loadOptions()

    return () => {
      active = false
    }
  }, [])

  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: pageSize,
      includeTotal: true,
      archive: statusFilter === 'inactive',
    }

    const search = buildSearchPayload(
      searchText,
      roleId,
      departmentId,
      companyId,
      roleOptions,
      departmentOptions,
      companyOptions,
    )
    if (search) params.search = search

    return params
  }, [companyId, companyOptions, departmentId, departmentOptions, page, pageSize, roleId, roleOptions, searchText, statusFilter])

  useEffect(() => {
    let active = true

    async function loadUsers() {
      setIsLoading(true)
      setError('')

      try {
        const response = await listUsers(queryParams)
        if (!active) return

        const rows = Array.isArray(response?.items) ? response.items : []
        setItems(rows)
        setMeta({
          total: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load user directory.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadUsers()

    return () => {
      active = false
    }
  }, [page, pageSize, queryParams])

  useEffect(() => {
    let active = true
    getMasterApprovalSummary({ module_key: 'users' })
      .then((summary) => {
        if (active) setPendingApprovalCount(Number(summary?.pending || 0))
      })
      .catch(() => {
        if (active) setPendingApprovalCount(0)
      })
    return () => {
      active = false
    }
  }, [])

  const rows = useMemo(
    () =>
      items.map((item) => {
        const status = normalizeStatus(item)
        return {
          id: Number(item.id),
          userCode: item.user_code || '-',
          name: item.name || '-',
          email: item.email || '-',
          roleName: item.role_name || '-',
          companyName: item.company_name || '-',
          branchName: item.branch_name || '-',
          departmentName: item.department_name || '-',
          reportingManagerName: item.reporting_manager_name || '-',
          reportingManagerInitials: String(item.reporting_manager_name || 'NA')
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0])
            .join('')
            .toUpperCase(),
          reportingManagerPhotoUrl: toPhotoUrl(item.reporting_manager_photo_path),
          designation: item.designation || '',
          photoUrl: toPhotoUrl(item.profile_photo_path),
          dataScope: String(item.data_scope || 'SELF').toUpperCase(),
          statusLabel: status.label,
          statusTone: status.tone,
          isArchived: Number(item.is_archive) === 1,
          isLocked: Number(item.is_locked) === 1,
          lastLogin: item.last_login || null,
        }
      }),
    [items],
  )

  const totalPages = useMemo(() => {
    if (!meta.total || !meta.pageSize) return meta.hasMore ? page + 1 : Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!rows.length) return 0
    return (page - 1) * pageSize + 1
  }, [page, pageSize, rows.length])

  const rangeEnd = useMemo(() => {
    if (!rows.length) return 0
    return rangeStart + rows.length - 1
  }, [rangeStart, rows.length])

  const stats = useMemo(() => {
    const activeUsers = rows.filter((row) => row.statusLabel === 'Active').length
    const contractors = rows.filter((row) => /contractor/i.test(row.designation) || /contractor/i.test(row.roleName)).length
    const pendingApproval = pendingApprovalCount
    const loggedUsers = rows.filter((row) => Boolean(row.lastLogin)).length
    const avgLoginDepth = rows.length ? ((loggedUsers / rows.length) * 100).toFixed(1) : '0.0'

    return {
      activeUsers,
      contractors,
      pendingApproval,
      avgLoginDepth,
    }
  }, [pendingApprovalCount, rows])

  function setSearchTextAndReset(value) {
    setSearchText(value)
    setPage(1)
  }

  function setRoleIdAndReset(value) {
    setRoleId(value)
    setPage(1)
  }

  function setDepartmentIdAndReset(value) {
    setDepartmentId(value)
    setPage(1)
  }

  function setCompanyIdAndReset(value) {
    setCompanyId(value)
    setPage(1)
  }

  function setStatusFilterAndReset(value) {
    setStatusFilter(value)
    setPage(1)
  }

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setPage(nextPage)
  }

  function goToPreviousPage() {
    setPage((prev) => Math.max(1, prev - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) {
      setPage((prev) => prev + 1)
    }
  }

  return {
    state: {
      searchText,
      roleId,
      departmentId,
      companyId,
      statusFilter,
      roleOptions,
      departmentOptions,
      companyOptions,
      page,
      totalPages,
      totalCount: meta.total,
      hasMore: meta.hasMore,
      isLoading,
      error,
      rows,
      rangeStart,
      rangeEnd,
      stats,
    },
    actions: {
      setSearchText: setSearchTextAndReset,
      setRoleId: setRoleIdAndReset,
      setDepartmentId: setDepartmentIdAndReset,
      setCompanyId: setCompanyIdAndReset,
      setStatusFilter: setStatusFilterAndReset,
      goToPage,
      goToPreviousPage,
      goToNextPage,
    },
  }
}
